import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { NoDataError } from "common/components/NoDataError";
import { SimpleTabs } from "common/components/tabs/SimpleTabs";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { FeedbackListItem } from "domain/feedback/FeedbackListItem";
import {
  getFeedbackTitle,
  isAnswered,
  sortFeedbackList,
} from "domain/feedback/util";
import { teacherQuestionsBreadcrumb } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import * as React from "react";
import { GetTeacherQuestionsPageData } from "__generated__/types";

const DATA_QUERY = gql`
  query GetTeacherQuestionsPageData {
    feedbackList {
      id
      messages {
        submittedAt
        submittedBy {
          id
        }
        body {
          isMarkdown
          content
        }
      }
      givenOn {
        ... on FeedbackGivenOnFolder {
          elementId
          folder {
            id
            title
          }
        }
        ... on FeedbackGivenOnKurssloeyfe {
          elementId
          kurssloeyfe {
            id
            title
          }
        }
      }
    }
  }
`;

export const TeacherQuestionsPage: React.FC = () => {
  const { loadingOrError, data } =
    useSimpleQuery<GetTeacherQuestionsPageData>(DATA_QUERY);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.feedbackList) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const sortedFeedbackList = [...data.feedbackList];
  sortedFeedbackList.sort(sortFeedbackList);

  const answeredFeedbacks = sortedFeedbackList.filter(
    (e) => isAnswered(e) && !e.givenOn,
  );
  const unAnsweredFeedbacks = sortedFeedbackList.filter(
    (e) => !isAnswered(e) && !e.givenOn,
  );

  return (
    <NormalContentLayout
      title="Innboks for spørsmål"
      breadcrumbItems={teacherQuestionsBreadcrumb}
    >
      <SimpleTabs
        content={[
          {
            title: "Ubesvarte",
            content: (
              <>
                {unAnsweredFeedbacks.length === 0 ? (
                  <p>Ingen ubesvarte spørsmål</p>
                ) : (
                  <ListContainer>
                    {unAnsweredFeedbacks.map((feedback) => (
                      <ListItem key={feedback.id}>
                        <FeedbackListItem
                          id={feedback.id}
                          title={getFeedbackTitle(feedback)}
                          canAnswer={true}
                          isQuestion={!feedback.givenOn}
                          answerButtonTitle="Svar"
                        />
                      </ListItem>
                    ))}
                  </ListContainer>
                )}
              </>
            ),
          },
          {
            title: "Besvarte",
            content: (
              <>
                {answeredFeedbacks.length === 0 ? (
                  <p>Ingen besvarte spørsmål</p>
                ) : (
                  <ListContainer>
                    {answeredFeedbacks.map((feedback) => (
                      <ListItem key={feedback.id}>
                        <FeedbackListItem
                          id={feedback.id}
                          title={getFeedbackTitle(feedback)}
                          canAnswer={true}
                          isQuestion={!feedback.givenOn}
                          answerButtonTitle="Svar"
                        />
                      </ListItem>
                    ))}
                  </ListContainer>
                )}
              </>
            ),
          },
        ]}
      />
    </NormalContentLayout>
  );
};
